import { AddCircleOutlineOutlined, Clear, InfoOutlined, Person } from '@mui/icons-material';
import {
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	Select,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import { useContext, useRef, useState } from 'react';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { UserContext } from '../../../contexts/UserContext';
import { AuthTypes } from '../../../models/suppressionAuthTypes';
import PostmanTextField from './postmanTextField';
// import ReactJson from 'react-json-view';
import { SuppressionErrorDefaults } from "../../../models/suppressionResults";

export const methods = {
	POST: "POST",
	GET: "GET"
};
export const responseTypes = {
	Boolean: "Boolean",
	Number: "Number",
	String: "String"
};
export const stringTransformations = {
	ToUpper: {
		name: "ToUpper",
		display: "To uppercase",
		transform: value => value.toUpperCase()
	},
	ToLower: {
		name: "ToLower",
		display: "To lowercase",
		transform: value => value.toLowerCase()
	},
	None: {
		name: "None",
		display: "None",
		transform: value => value
	}
};

const exampleContact = {
	"zip": "14472",
	"customFields": {
		"balance": 656
	},
	"lastName": "Cooper",
	"createdAt": "2021-06-10T20:10:38.270Z",
	"source": "CDYX",
	"updatedBy": "joseph.cooper@what.com",
	"firstName": "Joseph",
	"state": "NY",
	"city": "Honeoy Falls",
	"timeZoneBounds": {
		"max": {
			"offset": "-7",
			"dst": true
		},
		"min": {
			"offset": "-5",
			"dst": true
		}
	},
	"externalId": "13406",
	"updatedAt": "2021-06-24T18:01:03.117Z",
	"phone": "9990370004",
	"cell": "9990370008"
};


// This UI was eyeballed off of Postman 😛
export default function RestService({ formikProps, logs, setLogs, checkIndex }) {
	const [viewPassword, setViewPassword] = useState(false);
	const [selectedTab, setSelectedTab] = useState(0);
	const userContext = useContext(UserContext);
	const contactAttributePlaceholders = [
		{ displayName: 'First Name', name: 'firstName' },
		{ displayName: 'Last Name', name: 'lastName' },
		{ displayName: 'Source', name: 'source' },
		{ displayName: 'Phone', name: 'phone' },
		{ displayName: 'Cell', name: 'cell' },
		{ displayName: 'Email', name: 'email' },
		{ displayName: 'City', name: 'city' },
		{ displayName: 'State', name: 'state' },
		{ displayName: 'Zip', name: 'zip' },
		...((userContext && userContext.customFields) || [])
	];

	// read via checks[checkIndex]
	const restObject = formikProps.values.checks?.[checkIndex]?.restSuppression || {};

	const baseName = (path) => `checks.${checkIndex}.restSuppression.${path}`;

	function handleAuthTypeChange(e) {
		// ensure Formik knows the field that changed
		const synthetic = { ...e, target: { ...e.target, name: baseName('auth.type') } };
		formikProps.handleChange(synthetic);

		const val = e.target.value;
		const infoPath = baseName('auth.info');

		// initialize appropriate info shape depending on type
		switch (val) {
			case AuthTypes[1]: // API key style -> array with one key/value
				formikProps.setFieldValue(infoPath, [{ key: 'x-api-key', value: '', addTo: 'header', description: '' }]);
				break;
			case AuthTypes[2]: // token style
				formikProps.setFieldValue(infoPath, [{ token: '' }]);
				break;
			case AuthTypes[3]: // username/password
				formikProps.setFieldValue(infoPath, [{ username: '', password: '' }]);
				break;
			default:
				// None or unknown -> empty array
				formikProps.setFieldValue(infoPath, []);
				break;
		}
	}

	function handleResponseTypeChange(e) {
		const synthetic = { ...e, target: { ...e.target, name: baseName('response.type') } };
		formikProps.handleChange(synthetic);

		const val = e.target.value;
		const valuePath = baseName('response.value.value');

		switch (val) {
			case responseTypes.Boolean:
				formikProps.setFieldValue(valuePath, true);
				break;
			case responseTypes.Number:
				formikProps.setFieldValue(valuePath, 0);
				break;
			case responseTypes.String:
				formikProps.setFieldValue(valuePath, '', false);
				break;
			default:
				break;
		}
	}

	return (
		<Grid container spacing={2} direction='column'>
			<Grid item>
				<Grid container direction='row'>
					<Grid item>
						<FormControl>
							<Select
								variant='outlined'
								style={{ borderRadius: '4px 0px 0px 4px', width: '100px' }}
								SelectDisplayProps={{
									style: {
										borderRadius: '4px 0px 0px 4px'
									}
								}}
								value={restObject.method || ''}
								onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('method') } })}
								onBlur={formikProps.handleBlur}
								name={baseName('method')}
							>
								{Object.keys(methods).map((method, index) =>
									<MenuItem key={index} value={method}>{method}</MenuItem>)}
							</Select>
						</FormControl>
					</Grid>
					<Grid item style={{ flexGrow: 1 }}>
						<TextField
							variant='outlined'
							label='Endpoint'
							value={restObject.endpoint || ''}
							name={baseName('endpoint')}
							onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('endpoint') } })}
							onBlur={formikProps.handleBlur}
							error={formikProps.errors?.checks?.[checkIndex]?.restSuppression?.endpoint && formikProps.touched?.checks?.[checkIndex]?.restSuppression?.endpoint}
							helperText={formikProps.touched?.checks?.[checkIndex]?.restSuppression?.endpoint && formikProps.errors?.checks?.[checkIndex]?.restSuppression?.endpoint}
							InputProps={{
								style: {
									borderTopLeftRadius: '0px',
									borderBottomLeftRadius: '0px'
								}
							}}
						/>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
					<Tab value={0} label='Authorization' />
					<Tab value={1} label='Headers' />
					<Tab value={4} label='Body' disabled={restObject.method !== "POST"} />
					<Tab value={2} label='Params' />
					<Tab value={3} label='Settings' />
					<Tab value={5} label='Logs' />
				</Tabs>
				<Divider />
			</Grid>

			{/* Authorization */}
			{selectedTab === 0 &&
				<Grid item>
					<Grid container spacing={4}>
						<Grid item>
							<FormControl variant='outlined'>
								<InputLabel id='auth-type-label'>Type</InputLabel>
								<Select
									defaultValue={AuthTypes[0]}
									labelId='auth-type-label'
									label='Type'
									onChange={handleAuthTypeChange}
									name={baseName('auth.type')}
									value={restObject.auth?.type || AuthTypes[0]}
								>
									{AuthTypes.map((type, index) =>
										<MenuItem key={index} value={type}>{type.replace(/_/g, ' ')}</MenuItem>)}
								</Select>
							</FormControl>
						</Grid>

						{restObject.auth?.type !== AuthTypes[0] &&
							<Grid item>
								<Divider orientation='vertical' />
							</Grid>}

						{/* auth.info is an array — reuse KeyValueDescriptionFields for it */}
						{restObject.auth?.type && restObject.auth.type !== AuthTypes[0] &&
							<Grid item style={{ width: '100%' }}>
								<KeyValueDescriptionFields
									formikProps={formikProps}
									propertyName={'auth.info'}
									checkIndex={checkIndex}
									label={'Authorization Info'}
									contactAttributePlaceholders={contactAttributePlaceholders}
									userContext={userContext}
									isAuth
								/>
							</Grid>}
					</Grid>
				</Grid>}

			{/* Headers */}
			{selectedTab === 1 &&
				<Grid item>
					<KeyValueDescriptionFields
						formikProps={formikProps}
						propertyName={'headers'}
						checkIndex={checkIndex}
						label={'Headers'}
						contactAttributePlaceholders={contactAttributePlaceholders}
						userContext={userContext}
					/>
				</Grid>}

			{/* Params */}
			{selectedTab === 2 &&
				<Grid item>
					<KeyValueDescriptionFields
						formikProps={formikProps}
						propertyName={'params'}
						checkIndex={checkIndex}
						label={'Query Params'}
						contactAttributePlaceholders={contactAttributePlaceholders}
						userContext={userContext}
					/>
				</Grid>}

			{/* Settings */}
			{selectedTab === 3 &&
				<Grid item>
					<Grid container spacing={2} direction='column'>
						<Grid item>
							<Grid container alignItems='center'>
								<Grid item>
									<TextField
										label='Response key'
										onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('response.path') } })}
										onBlur={formikProps.handleBlur}
										value={restObject?.response?.path || ''}
										name={baseName('response.path')}
										error={formikProps.errors?.checks?.[checkIndex]?.restSuppression?.response?.path && formikProps.touched?.checks?.[checkIndex]?.restSuppression?.response?.path}
										helperText={formikProps.touched?.checks?.[checkIndex]?.restSuppression?.response?.path && formikProps.errors?.checks?.[checkIndex]?.restSuppression?.response?.path}
									/>
								</Grid>
								<Grid item>
									<Tooltip title={<>Dot paths and array indexes are supported. The absence of this key in the response will be considered an error.</>}>
										<InfoOutlined color='primary' />
									</Tooltip>
								</Grid>
							</Grid>
						</Grid>

						<Grid item>
							<FormControl>
								<InputLabel id='suppress-on-label'>Response type</InputLabel>
								<Select name={baseName('response.type')} onChange={handleResponseTypeChange} value={restObject?.response?.type || ''}>
									{Object.keys(responseTypes).map(x =>
										<MenuItem key={x} value={x}>{x}</MenuItem>)}
								</Select>
							</FormControl>
						</Grid>

						<Grid item>
							{function () {
								switch (restObject?.response?.type) {
									case responseTypes.Boolean:
										return (
											<FormControl>
												<InputLabel id='suppress-on-label'>Response value</InputLabel>
												<Select
													name={baseName('response.value.value')}
													onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('response.value.value') } })}
													value={restObject?.response?.value?.value}
												>
													<MenuItem value={true}>True</MenuItem>
													<MenuItem value={false}>False</MenuItem>
												</Select>
											</FormControl>
										);
									case responseTypes.Number:
										return (
											<TextField
												label='Response value'
												onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('response.value.value') } })}
												onBlur={formikProps.handleBlur}
												value={restObject?.response?.value?.value ?? ''}
												name={baseName('response.value.value')}
												type='number'
												error={formikProps.errors?.checks?.[checkIndex]?.restSuppression?.response?.value?.value && formikProps.touched?.checks?.[checkIndex]?.restSuppression?.response?.value?.value}
												helperText={formikProps.touched?.checks?.[checkIndex]?.restSuppression?.response?.value?.value && formikProps.errors?.checks?.[checkIndex]?.restSuppression?.response?.value?.value}
											/>
										);
									case responseTypes.String:
										return (
											<Grid container spacing={2} alignItems='center'>
												<Grid item>
													<TextField
														label='Response value'
														onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('response.value.value') } })}
														onBlur={formikProps.handleBlur}
														value={restObject?.response?.value?.value ?? ''}
														name={baseName('response.value.value')}
														error={formikProps.errors?.checks?.[checkIndex]?.restSuppression?.response?.value?.value && formikProps.touched?.checks?.[checkIndex]?.restSuppression?.response?.value?.value}
														helperText={formikProps.touched?.checks?.[checkIndex]?.restSuppression?.response?.value?.value && formikProps.errors?.checks?.[checkIndex]?.restSuppression?.response?.value?.value}
													/>
												</Grid>
												<Grid item>
													<FormControl>
														<InputLabel>Response value transformation</InputLabel>
														<Select
															name={baseName('response.options.responseStringTransformation')}
															onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('response.options.responseStringTransformation') } })}
															value={restObject?.response?.options?.responseStringTransformation || ''}
														>
															{Object.keys(stringTransformations).map(transformation =>
																<MenuItem key={transformation} value={transformation}>{stringTransformations[transformation].display}</MenuItem>)}
														</Select>
													</FormControl>
												</Grid>
											</Grid>
										);
									default:
										return null;
								}
							}()}
						</Grid>

						<Grid item>
							<FormControlLabel
								label='Suppress on error'
								control={<Switch checked={restObject.suppressOnError} onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('suppressOnError') } })} name={baseName('suppressOnError')} color='primary' />}
							/>
						</Grid>

						{restObject.suppressOnError &&
							<Grid item>
								<FormControl>
									<InputLabel id='disposition-on-error-label'>Disposition On Error</InputLabel>
									<Select
										name={baseName('dispositionOnError')}
										onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: baseName('dispositionOnError') } })}
										value={restObject.dispositionOnError || ''}
									>
										{SuppressionErrorDefaults.map((result, index) =>
											<MenuItem key={index} value={result.dynamo}>{result.display}</MenuItem>)}
									</Select>
								</FormControl>
							</Grid>}
					</Grid>
				</Grid>}

			{/* Body preview for POST */}
			{selectedTab === 4 && restObject.method === "POST" &&
				<Grid item>
					<Grid container direction='column' spacing={2}>
						<Grid item>
							<Grid container spacing={2} alignContent='center'>
								<Grid item>
									<InfoOutlined color='primary' />
								</Grid>
								<Grid item>
									<Typography variant='body1'>The post body will contain all the contact info with the exception of the id and the tenant. Please see below for an example.</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							<JSONPretty id="json-pretty" data={exampleContact} />
						</Grid>
					</Grid>
				</Grid>}

			{/* Logs */}
			{selectedTab === 5 &&
				<Grid item>
					{logs.length === 0 &&
						<Typography variant='h6'>No data to display</Typography>}
					{logs.length > 0 &&
						<>
							<Grid container direction='column' spacing={2}>
								<Grid item>
									<Grid container justifyContent='space-between'>
										<Grid item>
											<Grid container spacing={2} alignItems='center'>
												<Grid item>
													<InfoOutlined color='primary' />
												</Grid>
												<Grid item>
													<Typography variant='body1'>These logs are only for localized testing. They do not get stored anywhere once you leave this page.</Typography>
												</Grid>
											</Grid>
										</Grid>
										<Grid item>
											<Button style={{ marginTop: '16px' }} variant='contained' color='primary' onClick={() => setLogs([])}>Clear</Button>
										</Grid>
									</Grid>
								</Grid>
								<Grid item style={{ overflowY: 'auto', maxHeight: '600px', marginBottom: '15px' }}>
									<TableContainer>
										<Table>
											<TableHead>
												<TableRow>
													<TableCell>Timestamp</TableCell>
													<TableCell>Contact</TableCell>
													<TableCell>Result</TableCell>
													<TableCell>Reason</TableCell>
													<TableCell>Response/Error</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{logs.map((log, index) => {
													const clone = {};
													for (const key in log.data) {
														clone[key] = log.data[key];
													}
													log.data = clone;

													return (
														<TableRow key={index} style={{ verticalAlign: 'baseline' }}>
															<TableCell style={{ width: '10%' }}>{(new Date(log.timestamp)).toLocaleString()}</TableCell>
															<TableCell style={{ width: '10%' }}>{`${log.contact.firstName} ${log.contact.lastName}`}</TableCell>
															<TableCell style={{ width: '10%' }}>{log.result}</TableCell>
															<TableCell style={{ width: '20%' }}>{log.reason}</TableCell>
															{/* <TableCell style={{ width: '50%' }}><ReactJson id="json-pretty2" src={log.data} collapsed={true} theme="monokai" style={{ overflowWrap: 'anywhere' }} /></TableCell> */}
														</TableRow>
													)
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Grid>
							</Grid>
						</>}
				</Grid>}
		</Grid>
	);
}



/* -------------------------------------------------------------------------
   KeyValueDescriptionFields
   Reusable for headers, params, and auth.info (array)
   Expects propertyName to be exactly the path segment under restSuppression,
   i.e. "headers", "params", or "auth.info"
   ------------------------------------------------------------------------- */
function KeyValueDescriptionFields(props) {
	const { formikProps, propertyName, checkIndex, label, contactAttributePlaceholders, userContext } = props;

	const [openMenuAnchorIndex, setOpenMenuAnchorIndex] = useState(-1);
	const deleteButtonRefs = useRef({});
	const insertPlaceholderButtonRefs = useRef({});

	// derive restObject locally
	const restObject = formikProps.values.checks?.[checkIndex]?.restSuppression || {};

	// flatten custom field names for placeholder insertion
	let names = [];
	for (const customField of (userContext?.customFields || [])) {
		if (customField && customField.name) names.push(customField.name);
	}

	function handleDeleteMouseEnter(index) {
		if (deleteButtonRefs.current[index]) deleteButtonRefs.current[index].style.display = 'block';
	}

	function handleDeleteMouseLeave(index) {
		if (deleteButtonRefs.current[index]) deleteButtonRefs.current[index].style.display = 'none';
	}

	function handleInsertPlaceholderMouseEnter(index) {
		if (insertPlaceholderButtonRefs.current[index]) insertPlaceholderButtonRefs.current[index].style.display = 'block';
	}

	function handleInsertPlaceholderMouseLeave(index) {
		if (insertPlaceholderButtonRefs.current[index]) insertPlaceholderButtonRefs.current[index].style.display = 'none';
	}

	function handleInsertPlaceholder(text) {
		const idx = openMenuAnchorIndex;
		if (idx < 0) {
			setOpenMenuAnchorIndex(-1);
			return;
		}

		const existingValue = restObject?.[propertyName]?.[idx]?.value ?? '';
		let newValue;
		if (names.includes(text)) {
			newValue = `${existingValue}{{custom:${text}}}`;
		} else {
			newValue = `${existingValue}{{${text}}}`;
		}

		const fieldPath = `checks.${checkIndex}.restSuppression.${propertyName}[${idx}].value`;
		formikProps.setFieldValue(fieldPath, newValue);
		setOpenMenuAnchorIndex(-1);
	}

	// ensure the array exists so FieldArray works correctly
	const arrayAtPath = restObject?.[propertyName];
	const safeArray = Array.isArray(arrayAtPath) ? arrayAtPath : [];

	return (
		<div style={{ marginTop: 8 }}>
			<Grid container alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
				<Grid item>
					<Typography variant="h6">{label}</Typography>
				</Grid>
				<Grid item />
			</Grid>

			<table style={{ width: '100%', borderCollapse: 'collapse' }}>
				<thead>
					<tr>
						<td style={{ fontWeight: 600 }}>Key</td>
						<td style={{ fontWeight: 600 }}>Value</td>
						<td style={{ fontWeight: 600 }}>Description</td>
						<td />
					</tr>
				</thead>
				<tbody>
					{safeArray.map((row, index) =>
						<tr key={index} onMouseEnter={() => handleDeleteMouseEnter(index)} onMouseLeave={() => handleDeleteMouseLeave(index)}>
							<td style={{ verticalAlign: 'top', paddingRight: 8 }}>
								<PostmanTextField
									value={row.key ?? ''}
									name={`checks.${checkIndex}.restSuppression.${propertyName}[${index}].key`}
									onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: `checks.${checkIndex}.restSuppression.${propertyName}[${index}].key` } })}
									onBlur={formikProps.handleBlur}
									placeholder='Key'
								/>
							</td>
							<td style={{ verticalAlign: 'top' }} onMouseEnter={() => handleInsertPlaceholderMouseEnter(index)} onMouseLeave={() => handleInsertPlaceholderMouseLeave(index)}>
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<PostmanTextField
										value={row.value ?? ''}
										name={`checks.${checkIndex}.restSuppression.${propertyName}[${index}].value`}
										onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: `checks.${checkIndex}.restSuppression.${propertyName}[${index}].value` } })}
										onBlur={formikProps.handleBlur}
										placeholder='Value'
										style={{ flexGrow: 1 }}
									/>
									<div ref={ref => { insertPlaceholderButtonRefs.current[index] = ref; }} style={{ display: 'none', marginLeft: 6 }}>
										<Tooltip title='Insert Placeholder' TransitionProps={{ timeout: 0 }} leaveTouchDelay={0} enterTouchDelay={0} enterDelay={0}>
											<IconButton size='small' onClick={() => setOpenMenuAnchorIndex(index)}>
												<Person fontSize='small' />
											</IconButton>
										</Tooltip>
									</div>
								</div>
							</td>
							<td style={{ verticalAlign: 'top' }}>
								<PostmanTextField
									value={row.description ?? ''}
									name={`checks.${checkIndex}.restSuppression.${propertyName}[${index}].description`}
									onChange={e => formikProps.handleChange({ ...e, target: { ...e.target, name: `checks.${checkIndex}.restSuppression.${propertyName}[${index}].description` } })}
									onBlur={formikProps.handleBlur}
									placeholder='Description'
								/>
							</td>
							<td style={{ verticalAlign: 'top' }}>
								<div ref={ref => { deleteButtonRefs.current[index] = ref; }} style={{ display: 'block' }}>
									<IconButton size='small' onClick={() => {
										// remove via FieldArray helpers - we need helpers here, but the original call site
										// that used FieldArray rendered a helpers prop. To keep parity, this delete button
										// should be wired to the FieldArray remove action. For simplicity, toggle the display,
										// and rely on the parent FieldArray add/remove buttons (the top Add). If you want
										// direct delete here, we can pass helpers as a prop.
										// As a fallback, manipulate the array directly:
										const arrPath = `checks.${checkIndex}.restSuppression.${propertyName}`;
										const newArr = (formiк => { }).apply; // no-op placeholder
										// We will instead trigger a filter-based set for removing item:
										const fullArray = (formikProps.values.checks?.[checkIndex]?.restSuppression?.[propertyName] || []).slice();
										fullArray.splice(index, 1);
										formikProps.setFieldValue(`checks.${checkIndex}.restSuppression.${propertyName}`, fullArray);
									}}>
										<Clear fontSize='small' />
									</IconButton>
								</div>
							</td>
						</tr>)}
				</tbody>
			</table>

			{/* Add button */}
			<div style={{ marginTop: 8 }}>
				<Button
					size="small"
					variant="outlined"
					onClick={() => {
						const arr = formikProps.values.checks?.[checkIndex]?.restSuppression?.[propertyName] || [];
						const newArr = arr.slice();
						newArr.push({ key: '', value: '', description: '' });
						formikProps.setFieldValue(`checks.${checkIndex}.restSuppression.${propertyName}`, newArr);
					}}
				>
					<AddCircleOutlineOutlined style={{ marginRight: 6 }} />
					Add New
				</Button>
			</div>

			<Menu anchorEl={insertPlaceholderButtonRefs.current[openMenuAnchorIndex]} open={Boolean(insertPlaceholderButtonRefs.current[openMenuAnchorIndex])} onClose={() => setOpenMenuAnchorIndex(-1)}>
				{(contactAttributePlaceholders || []).map((attribute, idx) =>
					<MenuItem key={idx} value={attribute.name} onClick={() => handleInsertPlaceholder(attribute.name)}>{attribute.displayName}</MenuItem>)}
			</Menu>
		</div>
	);
}
